import { RequestFilter } from "utils/client/CRUD/types";
import {
  GenerateRetrieveConnectionsParams,
  GetParamsWithoutGeneric,
  CombineOptionsWithValueParams,
  CalculateValueParams,
  RetrieveConnectionsParams,
  OptionsActionTypes,
  Options,
  OptionsAction,
  FormatOptionsWithRecordConverterParams,
  GenerateOptionsReducerParams,
  GenerateOnChangeParams,
  RetrieveConnectionsRequestParams,
  Option,
} from "./types";
import { AutocompleteChangeReason, AutocompleteChangeDetails, createFilterOptions } from "@mui/material";

export const generateSelectAllOption = (allSelected: boolean) => {
  return {
    id: "select-all",
    autocompleteLabel: allSelected ? "Deselect All" : "Select All",
  };
};

const retrieveConnectionsRequest = async <T extends GetParamsWithoutGeneric>({
  performingSearch,
  searchFields,
  inputValue,
  defaultGetParams,
  crud,
  defaultRetrievalAmount,
  exactMatch,
}: RetrieveConnectionsRequestParams<T>) => {
  const filterOperator = exactMatch ? "equals" : "contains";

  // if there is a search inputValue - create filters for all searchFields
  const searchFilters = performingSearch
    ? searchFields.map((field) => {
        return {
          field,
          operator: filterOperator,
          value: inputValue,
        };
      })
    : [];

  // combine with any defaultGetParams filters
  const combinedFilters: RequestFilter = {
    rules: {
      or: [...(defaultGetParams?.filters?.rules?.or || []), ...searchFilters],
      and: [...(defaultGetParams?.filters?.rules?.and || [])],
    },
  };

  const queries = {
    /* 
      if doing an exact match - set the limit to the number of fields we are searching 
        That is the number of possible exact match responses there could be
    */
    limit: exactMatch ? searchFields.length : defaultRetrievalAmount.toString(),
    page: "1",
    ...defaultGetParams.queries,
  };

  const getParams = {
    ...defaultGetParams,
    filters: combinedFilters,
    queries,
  };

  const response = await crud.get(getParams);

  return response;
};

const generateRetrieveConnections = <T extends GetParamsWithoutGeneric>({
  crud,
  haveRetrievedAll,
  setHaveRetrievedAll,
  defaultGetParams,
  inputValue,
  searchFields,
  responseData,
  convertRecordToOption,
  value,
  dispatchOptions,
  setLoading,
  options,
  setTotalRecords,
  retrieveErrorHandler,
}: GenerateRetrieveConnectionsParams<T>) => {
  return async (params?: RetrieveConnectionsParams) => {
    const { dependencyValueChange = false, dependencyFulfilled = true } = params ?? {};

    /* 
      Only get records if:
        1. We don't already have all and a dependency is not changing
            (changed dependency will trigger us replacing the options based on new dependency)
        2. The dependency is fulfilled (if dependency is needed - dependencyFulfilled defaults to true)
    */
    if ((haveRetrievedAll && !dependencyValueChange) || !dependencyFulfilled) {
      setLoading(false);
      return;
    }

    if (dependencyValueChange) {
      setHaveRetrievedAll(false);
    }

    const defaultRetrievalAmount = 25;

    try {
      const performingSearch = inputValue && !dependencyValueChange ? true : false;

      const response = await retrieveConnectionsRequest({
        performingSearch,
        searchFields,
        inputValue,
        defaultGetParams,
        crud,
        exactMatch: false,
        defaultRetrievalAmount,
      });

      const totalRecords = response.totalRecords;

      const formattedResponseRecords = response[responseData].map(convertRecordToOption);

      if (performingSearch) {
        // get records that are an exact match to the search
        const exactMatchResponse = await retrieveConnectionsRequest({
          performingSearch,
          searchFields,
          inputValue,
          defaultGetParams,
          crud,
          exactMatch: true,
          defaultRetrievalAmount,
        });

        if (exactMatchResponse[responseData].length) {
          const formattedExactMatchRecords = exactMatchResponse[responseData].map(convertRecordToOption);

          const responseIds: string[] = formattedResponseRecords.map((record: any) => record.id);

          // check if they were already part of the regular request response. If not, add them
          formattedExactMatchRecords.forEach((record: any) => {
            const match = responseIds.includes(record.id);

            if (!match) formattedResponseRecords.push(record);
          });
        }
      }

      if (!performingSearch) {
        setTotalRecords(totalRecords);
      }

      /* 
        We have retrieved all records if:
          - Response totalRecords is less than or equal to the amount we requested AND We are NOT performing a search
          - Response totalRecords is less than or equal to the number of options already retrieved AND We are NOT performing a search
      */
      if (!performingSearch && (totalRecords <= defaultRetrievalAmount || totalRecords <= options.length)) {
        setHaveRetrievedAll(true);
      }

      // combine retrieved with existing options and value(s)
      const finalOptions = combineOptionsWithValue({ options: formattedResponseRecords, value });

      // if dependency value changed - replace existing options with newly retrieved instead of combining
      const dispatchOptionsType = dependencyValueChange ? OptionsActionTypes.REPLACE : OptionsActionTypes.ADD;

      dispatchOptions({ type: dispatchOptionsType, payload: finalOptions });
    } catch (error) {
      console.error("Failed to retrieve connections", error);
      retrieveErrorHandler && retrieveErrorHandler(error);
    }

    setLoading(false);
  };
};

const combineOptionsWithValue = ({ options, value }: CombineOptionsWithValueParams) => {
  const combinedOptions: any[] = [...options];

  // combine options with value(s) - value can be a single option or an array of options if "multiple" is used on the Autocomplete
  if (Array.isArray(value)) {
    value.forEach((option) => {
      const match = combinedOptions.find((record) => record.id === option.id);

      if (!match) {
        combinedOptions.push(option);
      }
    });
  } else if (value) {
    const match = combinedOptions.find((record) => record.id === value.id);

    if (!match) {
      combinedOptions.push(value);
    }
  }

  return combinedOptions;
};

const formatOptionsWithRecordConverter = ({ options, convertRecordToOption }: FormatOptionsWithRecordConverterParams) => {
  const formattedOptions = options.map((option: any) => {
    // only format if needed
    if (option.autocompleteLabel) return option;

    return convertRecordToOption(option);
  });

  return formattedOptions;
};

const calculateValue = ({ value, convertRecordToOption }: CalculateValueParams) => {
  // value can be a single option or an array of options if "multiple" is used on the Autocomplete
  if (Array.isArray(value)) {
    const calculatedValue = value.map((selectedValue) => {
      if (selectedValue.autocompleteLabel) return selectedValue;

      const calculatedOption = convertRecordToOption(selectedValue);
      return calculatedOption;
    });

    return calculatedValue;
  } else if (value) {
    if (value.autocompleteLabel) return value;

    const calculatedValue = convertRecordToOption(value);

    return calculatedValue;
  } else {
    return null;
  }
};

const generateOptionsReducer = ({ convertRecordToOption }: GenerateOptionsReducerParams) => {
  return (state: Options, action: OptionsAction): Options => {
    switch (action.type) {
      case OptionsActionTypes.ADD:
        // combine records with existing options
        if (state.length) {
          const finalOptions: Options = action.payload.reduce((accumulated, option) => {
            const match = accumulated.find((record: any) => record.id === option.id);

            if (!match) accumulated.push(option);

            return accumulated;
          }, state as Options);

          const formattedFinalOptions = finalOptions.map((option: any) => {
            if (option.autocompleteLabel) return option;

            return convertRecordToOption(option);
          });

          return formattedFinalOptions;
        }

        const formattedPayload = action.payload
          .map((option: any) => {
            if (option.autocompleteLabel) return option;

            return convertRecordToOption(option);
          })
          .sort((a, b) => (a.autocompleteLabel > b.autocompleteLabel ? 1 : -1));

        return formattedPayload;
      case OptionsActionTypes.REPLACE:
        const formattedReplacePayload = action.payload
          .map((option: any) => {
            if (option.autocompleteLabel) return option;

            return convertRecordToOption(option);
          })
          .sort((a, b) => (a.autocompleteLabel > b.autocompleteLabel ? 1 : -1));

        return formattedReplacePayload;
      default:
        return state;
    }
  };
};

const generateOnChange = ({ onChange, value, dispatchOptions, options, isAllSelected, selectAllOption }: GenerateOnChangeParams) => {
  return (event: React.SyntheticEvent, value: any, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails) => {
    if (selectAllOption) {
      if (value.some((option: Option) => option.id === "select-all")) {
        const finalValue = isAllSelected ? [] : options;

        onChange && onChange(event, finalValue, reason, details);
        return;
      }
    }

    // When using freeSolo - options can be created. When this happens, we will properly format the new option and add it to options
    if (reason === "createOption") {
      const newOption = {
        autocompleteLabel: value,
      };

      dispatchOptions({
        type: OptionsActionTypes.ADD,
        payload: [newOption],
      });

      onChange && onChange(event, newOption, reason, details);
      return;
    }

    onChange && onChange(event, value, reason, details);
  };
};

const generateOption = createFilterOptions<any>();

const generateFilterOptions = () => {
  return (options: any, state: any) => {
    const autocompleteOptions = generateOption(options, state);

    const { inputValue } = state;

    const optionExists = options.some((option: any) => inputValue === option.autocompleteLabel);

    if (inputValue && !optionExists) {
      const newOption = {
        autocompleteLabel: inputValue,
      };

      autocompleteOptions.push(newOption);
    }

    return autocompleteOptions;
  };
};

export {
  generateRetrieveConnections,
  combineOptionsWithValue,
  calculateValue,
  generateOptionsReducer,
  formatOptionsWithRecordConverter,
  generateOnChange,
  generateFilterOptions,
};
